import React, { useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ExpertSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";

interface ExpertSectionProps {
  blok?: ExpertSection & SbBlokData;
}

const ExpertSectionComponent: React.FC<ExpertSectionProps> = ({ blok }) => {
  useEffect(() => {
    let carouselInstance: any;

    const initCarousel = async () => {
      if (typeof window !== 'undefined') {
        try {
          const bootstrap = await import('bootstrap');
          const carouselElement = document.getElementById('carouselExperts');
          if (carouselElement) {
            carouselInstance = new bootstrap.Carousel(carouselElement);
          }
        } catch (error) {
          console.error('Error initializing carousel:', error);
        }
      }
    };

    initCarousel();

    return () => {
      if (carouselInstance?.dispose) {
        carouselInstance.dispose();
      }
    };
  }, []);

  if (!blok) return null;

  const { title, description, team_members } = blok;

  if (!team_members?.length) {
    return null;
  }

  const hasMultipleExperts = team_members.length > 1;

  return (
    <div {...storyblokEditable(blok)} className="expert-section pb-5">
      <div className="container">
        {title && <h2 className="text-primary text-center mb-4">{title}</h2>}
        {description && <p className="text-center mb-3">{description}</p>}

        <div
          id="carouselExperts"
          className="carousel carousel-testimonial slide d-flex flex-column js-carousel-animate-height"
          data-bs-ride="carousel"
          data-bs-interval="10000"
        >
          {hasMultipleExperts && (
            <>
              <button
                className="carousel-control-prev rounded-circle lh-1 d-none d-lg-flex my-auto"
                type="button"
                data-bs-target="#carouselExperts"
                data-bs-slide="prev"
              >
                <i className="fas fa-chevron-left carousel-control-icon" />
                <span className="visually-hidden">Previous</span>
              </button>
              <button
                className="carousel-control-next rounded-circle lh-1 d-none d-lg-flex my-auto"
                type="button"
                data-bs-target="#carouselExperts"
                data-bs-slide="next"
              >
                <i className="fas fa-chevron-right carousel-control-icon" />
                <span className="visually-hidden">Next</span>
              </button>
            </>
          )}

          <div className="carousel-inner">
            {team_members.map((expert, index) => (
              <div
                key={expert._uid}
                className={`carousel-item ${index === 0 ? "active" : ""}`}
              >
                <div className="d-flex flex-column align-items-center text-center">
                  {expert.image?.filename && (
                    <div className="mb-2">
                      <Image
                        src={expert.image.filename}
                        alt={expert.name || ""}
                        width={160}
                        height={160}
                        className="js-lazy-load rounded-circle"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    </div>
                  )}

                  <div className="mb-4">
                    <div className="lead fw-bold text-dark mb-2">
                      {expert.name}
                      {expert.linkedin_url?.cached_url && (
                        <Link
                          href={expert.linkedin_url.cached_url}
                          className="ms-2 text-primary"
                          target="_blank"
                          rel="noopener"
                        >
                          <i className="fab fa-linkedin" />
                        </Link>
                      )}
                    </div>
                    {expert.position && (
                      <div className="text-muted lead">{expert.position}</div>
                    )}
                    {expert.colleges && (
                      <div className="text-muted small">{expert.colleges}</div>
                    )}
                  </div>

                  {expert.quote && (
                    <div className="col-lg-18 col-xl-14 mx-auto">
                      <blockquote className="unstyled text-center mb-4">
                        {expert.quote}
                      </blockquote>
                    </div>
                  )}

                  {expert.blog_link?.cached_url && (
                    <div className="mt-0">
                      <Link
                        className="btn btn-outline-primary"
                        href={expert.blog_link.cached_url}
                      >
                        Colleague spotlight
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {hasMultipleExperts && (
            <div className="carousel-indicators position-relative mt-4 mb-0">
              {team_members.map((_, index) => (
                <button
                  key={`indicator-${index}`}
                  type="button"
                  data-bs-target="#carouselExperts"
                  data-bs-slide-to={index}
                  className={`carousel-indicator rounded-circle border-top-0 border-bottom-0 ${
                    index === 0 ? "active" : ""
                  }`}
                  aria-current={index === 0 ? "true" : undefined}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertSectionComponent;